import { useEffect } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import useSignupContext from '@hooks/context/useSignupContext';
import { useFormContext, useWatch } from 'react-hook-form';
import DatePicker from '@components/date_picker';
import FormField from '@components/form_field';
import { getBirthDisabledDates, mapOccupationsToOptions } from '@utils/index';
import { EUserType } from '@xeppt/xeppt-sdk/types';
import Select from '@components/common/select';
import { countriesList } from '@_mock/index';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import personalIllustration from '@svg/illustrations/auth/personal.svg';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';
import { useDebounceValue } from 'usehooks-ts';

const PersonalDetailsSection = () => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'signup.personal_details'
    });
    const { submitLocale, labelLocale, placeholderLocale, validationLocale } = useLocales();
    const { nextStep, handleChangeIllustration } = useSignupContext();
    const { control, trigger, setError } = useFormContext();
    const type = useWatch({ name: 'type', control });
    const watchedTag = useWatch({ name: 'profile.tag', control });
    const [debouncedSearch] = useDebounceValue(watchedTag, 500);

    useEffect(() => {
        handleChangeIllustration(personalIllustration);
    }, []);

    const onSubmit = () => {
        trigger().then((isValid) => {
            if (isValid) {
                nextStep();
            }
        });
    };

    const { handleRequest: handleCheckTag } = useApiMutation({
        method: () => apiUserService.checkTag(watchedTag),
        onError: (e) => {
            setError('profile.tag', { message: validationLocale('tag') });
        }
    });

    useEffect(() => {
        if (debouncedSearch?.length >= 3) {
            handleCheckTag(undefined);
        }
    }, [debouncedSearch]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.steps}>STEP 6/{type === EUserType.BUSINESS ? '11' : '7'}</div>
            <Typography className={styles.title} variant="h3">
                {t('title')}
            </Typography>
            <div className={styles.form}>
                <FormField
                    name="profile.first_name"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            placeholder={placeholderLocale('enter_first_name')}
                            full
                            required
                            label={labelLocale('first_name')}
                        />
                    )}
                />
                <FormField
                    name="profile.last_name"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            placeholder={placeholderLocale('enter_last_name')}
                            full
                            required
                            label={labelLocale('last_name')}
                        />
                    )}
                />
                {type === EUserType.BUSINESS && (
                    <FormField<string | number>
                        name="profile.residence"
                        renderComponent={(props) => (
                            <Select
                                {...props}
                                full
                                isAllowSearch
                                placeholder={placeholderLocale('select_residence')}
                                label={labelLocale('residence')}
                                items={countriesList.map((item) => ({
                                    value: item,
                                    label: item
                                }))}
                            />
                        )}
                    />
                )}
                <FormField<string>
                    name="profile.tag"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            full
                            suffix="@"
                            label={labelLocale('tag')}
                            placeholder={placeholderLocale('enter_tag')}
                            className={styles.username}
                        />
                    )}
                />
                <FormField<string | number>
                    name="profile.occupancy"
                    renderComponent={(props) => (
                        <Select
                            items={mapOccupationsToOptions()}
                            isAllowSearch
                            full
                            placeholder={placeholderLocale('select_occupation')}
                            label={labelLocale('occupation')}
                            {...props}
                        />
                    )}
                />
                <FormField<Date | null>
                    name="profile.birth_date"
                    renderComponent={(props) => (
                        <DatePicker
                            {...props}
                            full
                            maxDate={getBirthDisabledDates()}
                            label={labelLocale('birth_date')}
                            isChange={false}
                        />
                    )}
                />
                <div className={styles.actions}>
                    <Button
                        className={styles.next}
                        onClick={onSubmit}
                        rightIcon="arrow_right"
                        variant="primary">
                        {submitLocale('next')}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default PersonalDetailsSection;
